import React, { useState } from 'react';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import QuickForm from '../components/forms/QuickForm';
import { coursesMock } from '../data/mock';
import { terminyMS, terminyMSByMonth } from '../data/terminy';
import { 
  Sparkles, Clock, Users, CheckCircle2,
  FileText, BookOpen, MessageSquare, Settings,
  Heart, Lightbulb, Phone, Calendar
} from 'lucide-react';

const course = coursesMock.ms;

export default function MSPage() {
  const [selectedTermin, setSelectedTermin] = useState('');

  const handleReservation = (termin) => {
    const terminValue = `#${termin.id} - ${termin.lekce1} a ${termin.lekce2} (${termin.cas} - ${termin.typ})`;
    setSelectedTermin(terminValue);
    
    // Scroll to form
    setTimeout(() => {
      const form = document.getElementById('reservation-form');
      form?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      <main>
        {/* Hero */}
        <section className="relative pt-32 pb-20 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-emerald-50 via-white to-teal-50/30" />
          <div className="absolute top-1/3 right-0 w-[500px] h-[500px] bg-emerald-100/40 rounded-full blur-3xl" />
          
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-3xl">
              <div className="flex flex-wrap gap-3 mb-6">
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-50 border border-emerald-100 rounded-full text-emerald-700 text-sm font-medium">
                  <Sparkles className="w-4 h-4" />
                  <span>Kurz pro mateřské školy</span>
                </div>
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-blue-50 border border-blue-100 rounded-full text-blue-700 text-sm font-medium">
                  <CheckCircle2 className="w-4 h-4" />
                  <span>V souladu s OP JAK II</span>
                </div>
              </div>

              <h1 className="text-4xl md:text-5xl font-bold text-slate-900 leading-tight mb-4">
                {course.title}
              </h1>
              
              <p className="text-xl md:text-2xl text-emerald-700 font-medium mb-4">
                {course.subtitle}
              </p>

              <p className="text-lg text-slate-600 mb-8 leading-relaxed">
                {course.tagline}
              </p>

              <div className="flex flex-wrap gap-6 mb-8">
                <div className="flex items-center gap-2 text-slate-700">
                  <Clock className="w-5 h-5 text-emerald-600" />
                  <span>{course.duration}</span>
                </div>
                <div className="flex items-center gap-2 text-slate-700">
                  <Users className="w-5 h-5 text-emerald-600" />
                  <span>Prezenčně min. {course.minParticipantsPresence} / Online bez omezení</span>
                </div>
                <div className="flex items-center gap-2 text-slate-700">
                  <span className="text-2xl font-bold text-slate-900">{course.price.toLocaleString('cs-CZ')} Kč</span>
                  <span className="text-slate-500">/ účastník</span>
                </div>
              </div>
            </div>

            {/* Quick Form in Hero */}
            <div className="mt-12 max-w-xl">
              <div className="bg-white rounded-2xl p-6 shadow-xl shadow-emerald-100/50 border border-emerald-100">
                <div className="flex items-center gap-2 mb-4">
                  <Phone className="w-5 h-5 text-emerald-600" />
                  <span className="font-semibold text-slate-900">Nechte nám kontakt, ozveme se</span>
                </div>
                <QuickForm courseType="ms" buttonText="Chci se dozvědět víc" variant="hero" />
              </div>
            </div>
          </div>
        </section>

        {/* Why section */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6">
                  Proč tento kurz vznikl
                </h2>
                <p className="text-lg text-slate-600 leading-relaxed mb-6">
                  Umělá inteligence může být v MŠ velmi užitečným pomocníkem – pokud se používá jednoduše, prakticky a s rozumem.
                </p>
                <p className="text-lg text-slate-600 leading-relaxed mb-8">
                  Ne jako samostatný výukový nástroj, ale jako <strong className="text-slate-900">opora pedagoga</strong> při přípravě činností, tvorbě materiálů a administrativě.
                </p>
                
                <div className="space-y-4">
                  <div className="flex items-center gap-3 text-slate-700">
                    <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
                      <Clock className="w-5 h-5 text-emerald-600" />
                    </div>
                    <span className="font-medium">Ušetří vám čas</span>
                  </div>
                  <div className="flex items-center gap-3 text-slate-700">
                    <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
                      <Lightbulb className="w-5 h-5 text-emerald-600" />
                    </div>
                    <span className="font-medium">Přinese nové nápady pro práci s dětmi</span>
                  </div>
                  <div className="flex items-center gap-3 text-slate-700">
                    <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
                      <FileText className="w-5 h-5 text-emerald-600" />
                    </div>
                    <span className="font-medium">Pomůže s administrativou</span>
                  </div>
                </div>
              </div>

              <div className="relative">
                <div className="bg-gradient-to-br from-emerald-100 via-teal-50 to-cyan-100 rounded-2xl p-12 shadow-2xl">
                  <div className="grid grid-cols-2 gap-6">
                    <div className="bg-white/80 backdrop-blur rounded-xl p-6 shadow-lg">
                      <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center mb-4 mx-auto">
                        <Sparkles className="w-6 h-6 text-emerald-600" />
                      </div>
                      <p className="text-center text-slate-700 font-medium">Tvorba materiálů</p>
                    </div>
                    <div className="bg-white/80 backdrop-blur rounded-xl p-6 shadow-lg">
                      <div className="w-12 h-12 rounded-full bg-teal-100 flex items-center justify-center mb-4 mx-auto">
                        <Heart className="w-6 h-6 text-teal-600" />
                      </div>
                      <p className="text-center text-slate-700 font-medium">Individualizace</p>
                    </div>
                    <div className="bg-white/80 backdrop-blur rounded-xl p-6 shadow-lg">
                      <div className="w-12 h-12 rounded-full bg-cyan-100 flex items-center justify-center mb-4 mx-auto">
                        <FileText className="w-6 h-6 text-cyan-600" />
                      </div>
                      <p className="text-center text-slate-700 font-medium">Administrativa</p>
                    </div>
                    <div className="bg-white/80 backdrop-blur rounded-xl p-6 shadow-lg">
                      <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center mb-4 mx-auto">
                        <Lightbulb className="w-6 h-6 text-emerald-600" />
                      </div>
                      <p className="text-center text-slate-700 font-medium">Inspirace</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* What you get */}
        <section className="py-20 bg-gradient-to-b from-slate-50 to-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center max-w-3xl mx-auto mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
                Co si z kurzu odnesete
              </h2>
              <p className="text-lg text-slate-600">
                Odcházíte s hotovými výstupy, které můžete okamžitě použít ve třídě.
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {course.benefits.map((benefit, index) => (
                <div key={index} className="bg-white rounded-xl p-6 shadow-sm border border-slate-100 hover:shadow-md hover:border-emerald-100 transition-all">
                  <CheckCircle2 className="w-6 h-6 text-emerald-500 mb-4" />
                  <p className="text-slate-700 leading-relaxed">{benefit}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Target audience */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-12">
              <div>
                <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6">
                  Pro koho je kurz určen
                </h2>
                <div className="space-y-4">
                  {course.targetAudience.map((target, index) => (
                    <div key={index} className="flex items-center gap-3 p-4 bg-slate-50 rounded-xl">
                      <CheckCircle2 className="w-5 h-5 text-emerald-500 flex-shrink-0" />
                      <span className="text-slate-700">{target}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6">
                  Co se v kurzu naučíte
                </h2>
                <div className="space-y-3">
                  {course.topics.map((topic, index) => (
                    <div key={index} className="flex items-start gap-3 p-4 bg-emerald-50/50 rounded-xl border border-emerald-100/50">
                      <div className="w-8 h-8 rounded-lg bg-emerald-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <span className="text-emerald-700 font-semibold text-sm">{index + 1}</span>
                      </div>
                      <span className="text-slate-700">{topic}</span>
                    </div>
                  ))}
                </div>
                <p className="mt-6 text-slate-600 italic">
                  Kurz je plně praktický – pracujete na vlastních tématech a materiálech.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Admin benefits */}
        <section className="py-20 bg-slate-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6">
                  Méně administrativy,<br />více času na děti
                </h2>
                <p className="text-lg text-slate-600 mb-8 leading-relaxed">
                  Součástí kurzu je využití AI jako administrativní opory pedagoga MŠ. 
                  Ukazujeme, jak si ušetřit čas při:
                </p>
                <div className="space-y-4">
                  {course.adminBenefits.map((benefit, index) => (
                    <div key={index} className="flex items-center gap-3">
                      <div className="w-2 h-2 rounded-full bg-emerald-500" />
                      <span className="text-slate-700">{benefit}</span>
                    </div>
                  ))}
                </div>
                <p className="mt-8 text-slate-500">
                  Cílem je omezit rutinní psaní a papírování, aby zůstal prostor na to nejdůležitější – práci s dětmi.
                </p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="bg-white rounded-xl p-6 shadow-lg border border-emerald-100">
                  <Settings className="w-8 h-8 text-emerald-500 mb-4" />
                  <p className="text-slate-900 font-medium">Pedagogické podklady</p>
                </div>
                <div className="bg-white rounded-xl p-6 shadow-lg border border-emerald-100">
                  <FileText className="w-8 h-8 text-teal-500 mb-4" />
                  <p className="text-slate-900 font-medium">Záznamy a hodnocení</p>
                </div>
                <div className="bg-white rounded-xl p-6 shadow-lg border border-emerald-100">
                  <BookOpen className="w-8 h-8 text-cyan-500 mb-4" />
                  <p className="text-slate-900 font-medium">IVP podklady</p>
                </div>
                <div className="bg-white rounded-xl p-6 shadow-lg border border-emerald-100">
                  <MessageSquare className="w-8 h-8 text-emerald-500 mb-4" />
                  <p className="text-slate-900 font-medium">Komunikace s rodiči</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* OP JAK */}
        <section className="py-20 bg-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-2xl p-8 md:p-12 border border-blue-100">
              <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-6">
                Vztah k OP JAK II
              </h2>
              <p className="text-lg text-slate-600 mb-6">
                Obsah kurzu je v souladu s tematickými okruhy OP JAK II, zejména v oblasti:
              </p>
              <div className="grid sm:grid-cols-2 gap-4 mb-8">
                {course.opJakInfo.areas.map((area, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <CheckCircle2 className="w-5 h-5 text-blue-500 flex-shrink-0" />
                    <span className="text-slate-700">{area}</span>
                  </div>
                ))}
              </div>
              <div className="bg-white/80 rounded-xl p-6 border border-blue-100">
                <p className="text-slate-700 font-medium mb-2">👉 {course.opJakInfo.note.split('.')[0]}.</p>
                <p className="text-slate-600">
                  Žádná spoluúčast, žádná žádost, žádné vykazování. Kurz je hrazen přímo mateřskou školou.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Pricing */}
        <section className="py-20 bg-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
                Jak kurz probíhá
              </h2>
            </div>

            <div className="bg-gradient-to-br from-emerald-50 to-teal-50 rounded-2xl p-8 md:p-12 border border-emerald-100">
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 mb-10">
                <div className="text-center">
                  <div className="w-14 h-14 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
                    <Clock className="w-7 h-7 text-emerald-600" />
                  </div>
                  <p className="text-sm text-slate-500 mb-1">Rozsah</p>
                  <p className="font-semibold text-slate-900 text-sm">{course.duration}</p>
                </div>
                <div className="text-center">
                  <div className="w-14 h-14 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
                    <Sparkles className="w-7 h-7 text-emerald-600" />
                  </div>
                  <p className="text-sm text-slate-500 mb-1">Forma</p>
                  <p className="font-semibold text-slate-900">{course.format}</p>
                </div>
                <div className="text-center">
                  <div className="w-14 h-14 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
                    <Users className="w-7 h-7 text-emerald-600" />
                  </div>
                  <p className="text-sm text-slate-500 mb-1">Min. účastníků</p>
                  <p className="font-semibold text-slate-900 text-sm">Prezenčně {course.minParticipantsPresence} / Online bez omezení</p>
                </div>
                <div className="text-center">
                  <div className="w-14 h-14 rounded-full bg-blue-100 flex items-center justify-center mx-auto mb-4">
                    <CheckCircle2 className="w-7 h-7 text-blue-600" />
                  </div>
                  <p className="text-sm text-slate-500 mb-1">Financování</p>
                  <p className="font-semibold text-slate-900">OP JAK II / DVPP</p>
                </div>
              </div>

              <div className="text-center bg-white rounded-xl p-8 shadow-sm">
                <p className="text-slate-600 mb-2">Cena kurzu</p>
                <p className="text-4xl font-bold text-slate-900 mb-2">
                  {course.price.toLocaleString('cs-CZ')} Kč
                  <span className="text-lg font-normal text-slate-500"> / účastník</span>
                </p>
                <p className="text-sm text-slate-500 mb-4">
                  Platba probíhá standardně fakturou na vaši školu.
                </p>
                <div className="flex flex-wrap gap-2 justify-center">
                  <span className="px-3 py-1 bg-emerald-50 text-emerald-700 rounded-full text-sm font-medium">
                    Financovatelné z OP JAK II
                  </span>
                  <span className="px-3 py-1 bg-blue-50 text-blue-700 rounded-full text-sm font-medium">
                    Financovatelné z DVPP
                  </span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Termíny školení */}
        <section className="py-20 bg-white">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
                Dostupné termíny 2026
              </h2>
              <p className="text-lg text-slate-600">
                Vyberte si termín a rezervujte místo
              </p>
            </div>

            {/* Termíny po měsících */}
            <div className="space-y-12">
              {Object.entries(terminyMSByMonth).map(([month, terminy]) => (
                <div key={month}>
                  <h3 className="text-xl font-bold text-slate-900 mb-4">
                    {month}
                  </h3>
                  
                  <div className="overflow-x-auto">
                    <table className="w-full border-collapse">
                      <thead>
                        <tr className="bg-slate-50">
                          <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700 border-b">#</th>
                          <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700 border-b">Lekce 1</th>
                          <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700 border-b">Lekce 2</th>
                          <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700 border-b">Čas</th>
                          <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700 border-b">Typ</th>
                          <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700 border-b"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {terminy.map((termin, index) => (
                          <tr key={termin.id} className={index % 2 === 0 ? 'bg-white' : 'bg-slate-50/50'}>
                            <td className="px-4 py-3 text-sm font-bold text-emerald-600 border-b">#{termin.id}</td>
                            <td className="px-4 py-3 text-sm text-slate-900 border-b">{termin.lekce1}</td>
                            <td className="px-4 py-3 text-sm text-slate-900 border-b">{termin.lekce2}</td>
                            <td className="px-4 py-3 text-sm text-slate-700 border-b">{termin.cas}</td>
                            <td className="px-4 py-3 border-b">
                              <span className={`inline-flex items-center px-2.5 py-1 rounded text-xs font-medium ${
                                termin.typ === 'ranní' 
                                  ? 'bg-amber-100 text-amber-700' 
                                  : 'bg-blue-100 text-blue-700'
                              }`}>
                                {termin.typ}
                              </span>
                            </td>
                            <td className="px-4 py-3 border-b">
                              <button
                                onClick={() => handleReservation(termin)}
                                className="bg-emerald-500 hover:bg-emerald-600 text-white text-sm font-medium px-4 py-2 rounded-lg transition-colors"
                              >
                                Rezervovat
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Big CTA Section */}
        <section id="reservation-form" className="py-20 bg-gradient-to-br from-emerald-600 to-teal-700">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-10">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                Máte zájem o kurz pro vaši školku?
              </h2>
              <p className="text-lg text-emerald-100">
                Nechte nám kontakt a my se vám ozveme s termíny a dalšími informacemi.
              </p>
            </div>

            <div className="bg-white rounded-2xl p-8 md:p-10 shadow-2xl">
              <QuickForm courseType="ms" buttonText="Chci se dozvědět víc" variant="hero" preselectedTermin={selectedTermin} />
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
